import React, { FormEvent, useState } from 'react';
import { Button, Stack, TextField, Typography } from '@mui/material';
import { ChangePasswordRequest } from 'src/model/api/v2/data-contracts';
import { useChangePasswordMutation } from 'src/api/v2/profile-settings-v2-ApiSlice';
import { SettingsSection } from 'src/pages/ProjectSettingsPage/components/SettingsSection';
import { PasswordIcon } from 'src/icons/PasswordIcon';
import { BaseDialog } from 'src/components/BaseDialog/BaseDialog';
import { getFrontendURL } from 'src/utils/url';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { ts } from 'src/utils/translate';
import { theme } from 'src/theme/mui-theme';

type PasswordField = keyof ChangePasswordRequest;
type PasswordErrors = Partial<Record<PasswordField, string>>;

const PASSWORD_MIN_LENGTH = 6;
const EMPTY_FORM: ChangePasswordRequest = { current_password: '', password: '', password_repeat: '' };

const validate = (form: ChangePasswordRequest): PasswordErrors => {
    const errors: PasswordErrors = {};
    const required = ts('jqval_msg_required', 'This field is required.');

    if (form.current_password === '') errors.current_password = required;

    if (form.password === '') {
        errors.password = required;
    } else if (form.password.length < PASSWORD_MIN_LENGTH) {
        errors.password = ts('profile_settings_password_min_length', 'This field should contain at least 6 characters.');
    }

    if (form.password_repeat !== form.password) {
        errors.password_repeat = ts('profile_settings_password_mismatch', "The password doesn't match");
    }

    return errors;
};

export const PasswordSection = () => {
    const dispatch = useAppDispatch();
    const [changePassword, { isLoading }] = useChangePasswordMutation();
    const [form, setForm] = useState<ChangePasswordRequest>(EMPTY_FORM);
    const [formValidationErrors, setFormValidationErrors] = useState<PasswordErrors>({});
    const [isPasswordChanged, setPasswordChanged] = useState(false);

    // the password change logs out every session, this one included
    const redirectToLogin = () => {
        window.location.href = `${getFrontendURL()}/site/login`;
    };

    const handleChange = (field: PasswordField, value: string) => {
        setForm(prevForm => ({ ...prevForm, [field]: value }));
        setFormValidationErrors(prevErrors => ({ ...prevErrors, [field]: undefined }));
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();

        const validationErrors = validate(form);
        setFormValidationErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) return;

        changePassword(form)
            .unwrap()
            .then(() => {
                setForm(EMPTY_FORM);
                setPasswordChanged(true);
            })
            .catch((error) => {
                const errorParams = error?.data?.errorParams as PasswordErrors | null | undefined;
                if (errorParams) {
                    setFormValidationErrors(errorParams);
                }
                if (error?.data?.errorMessage == null) {
                    dispatch(showSnackBar({
                        text: ts('profile_settings_save_error', 'Failed to save profile settings'),
                        severity: 'error',
                    }));
                }
            });
    };

    const renderPasswordField = (field: PasswordField, label: string, autoComplete: string) => (
        <TextField
            id={`profile-${field}`}
            type="password"
            label={label}
            value={form[field]}
            autoComplete={autoComplete}
            error={!!formValidationErrors[field]}
            helperText={formValidationErrors[field]}
            fullWidth
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(event) => handleChange(field, event.target.value)}
        />
    );

    return (
        <SettingsSection title={ts('profile_settings_password_title', 'Password')} titleIcon={<PasswordIcon />}>
            <Stack component="form" spacing={3} onSubmit={handleSubmit} noValidate>
                <Typography variant="body2" color={theme.palette.text.secondary}>
                    {ts('profile_settings_change_password', 'Change password')}
                </Typography>
                {renderPasswordField('current_password', ts('profile_settings_current_password', 'Current password'), 'current-password')}
                {renderPasswordField('password', ts('profile_settings_password', 'Password'), 'new-password')}
                {renderPasswordField('password_repeat', ts('profile_settings_password_repeat', 'Confirm password'), 'new-password')}
                <Button
                    type="submit"
                    variant="outlined"
                    disabled={isLoading}
                    sx={{ alignSelf: 'flex-start', textTransform: 'uppercase' }}
                >
                    {ts('profile_settings_save_changes', 'Save changes')}
                </Button>
            </Stack>
            <BaseDialog
                open={isPasswordChanged}
                onClose={redirectToLogin}
                title={ts('profile_settings_password_title', 'Password')}
                maxWidth="xs"
                actionLabel={ts('plt_ok', 'OK')}
                onAction={redirectToLogin}
            >
                <Typography variant="body2" color={theme.palette.text.secondary}>
                    {ts('profile_settings_password_changed_log_in', 'Your password has been changed. Please log in again.')}
                </Typography>
            </BaseDialog>
        </SettingsSection>
    );
};
